import classNames from 'classnames'
import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { Editor } from 'primereact/editor'
import { Fieldset } from 'primereact/fieldset'
import { Message } from 'primereact/message'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { JournalFileDatatable } from './JournalFileDatatable'
import { claims } from '../../../claims'
import {
  API_LEARNING_JOURNAL,
  API_LEARNING_JOURNAL_EDIT,
  API_LEARNING_JOURNAL_REVIEW,
  API_LEARNING_JOURNALS,
  API_LEARNING_JOURNALS_SHORT,
} from '../../../constants'
import {
  HttpStatusCode,
  JournalRythmCt,
  RequestMethod,
} from '../../../model/enums/enums'
import { learningJournal } from '../../../model/learnJournal.model'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import {
  addLearningJournal,
  deleteLearningJournal,
  updateLearningJournal,
} from '../../../redux/official-document/learning-journal/learningJournalActions'
import { AuthRoutes } from '../../../router/routes'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { findApprenticeById } from '../../../util/arraySearch'
import { getFormattedDateForMySQL } from '../../../util/formatting'
import { hasRequiredClaim } from '../../../util/validation/hasClaim'
import { BackButton } from '../../util/button/BackButton'
import {
  getFormErrorMessage,
  getRequiredFormMessage,
} from '../../util/ErrorText'
import { ApprenticeDropdown } from '../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import { EmployeeDropdown } from '../../util/form/dropdown/person-dropdown/EmployeeDropdown'

const defaultLearningJournal: learningJournal = {
  entry_date: new Date().toString(),
  is_locked: false,
  hand_in_on_time: true,
}

dayjs.extend(weekOfYear)

export const JournalEntry = (): JSX.Element => {
  const toast = useRef<Toast>(null)

  const { personId, loadedBasicInformation } = useAppSelector(
    (state) => state.user
  )

  const userClaims = useAppSelector((state) => state.claims)

  const apprentices = useAppSelector((state) => state.apprentices)

  const currentApprentice = apprentices.find(
    (apprentice) => apprentice.person_id === personId
  )
  const employees = useAppSelector((state) => state.employees)

  const hasReviewClaim = hasRequiredClaim(
    userClaims,
    claims.learningJournals.review.update
  )
  const hasEditClaim = hasRequiredClaim(
    userClaims,
    claims.learningJournals.edit.update
  )

  const hasAdminClaim = hasReviewClaim && hasEditClaim

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: defaultLearningJournal,
    shouldFocusError: false,
  })

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  const dispatcher = useAppDispatch()

  const location = useLocation()
  const navigate = useNavigate()

  const journals = useAppSelector((state) => state.learningJournals)

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  const [lockedForReview, setLockedForReview] = useState<boolean>(false)

  const [currentJournal, setCurrentJournal] = useState<learningJournal>()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [showMonthDuplicateWarning, setShowMonthDuplicateWarning] =
    useState<boolean>(false)

  const reviewDisabled = !hasRequiredClaim(
    userClaims,
    claims.learningJournals.review.update
  )

  const reviewerCanEdit =
    hasReviewClaim && currentJournal?.is_locked && !currentJournal.completed
  const apprenticeCanEdit =
    hasEditClaim && !currentJournal?.is_locked && !currentJournal?.completed

  const showSaveButton =
    reviewerCanEdit || apprenticeCanEdit || hasAdminClaim || !currentJournal

  const helperMessages = (): string => {
    if (currentJournal?.completed) {
      return t<string>('journal.help_messages.closed')
    } else if (reviewerCanEdit) {
      return t<string>('journal.help_messages.apprentice_no_edit')
    } else if (hasEditClaim && hasReviewClaim && currentJournal?.is_locked) {
      return ''
    } else if (hasEditClaim && currentJournal?.is_locked) {
      return t<string>('journal.help_messages.apprentice_locked')
    } else if (hasReviewClaim && !currentJournal?.is_locked) {
      return t<string>('journal.help_messages.apprentice_edit')
    }

    return t<string>('journal.help_messages.edit')
  }

  const fetchFromDb = async (journal_id: number): Promise<void> => {
    const result = await handleRequest<learningJournal>(
      API_LEARNING_JOURNAL(journal_id),
      RequestMethod.GET
    )

    if (!result.data) {
      console.warn(
        '[Learning Journal] Failed to get Response, Learning Journal was not found'
      )
      navigate(AuthRoutes.journals)
    }

    // this is a stupid check but primefaces cannot seem to handle 1 and 0 in the checkbox
    // @ts-ignore
    const hand_in_on_time = result.data?.hand_in_on_time !== 0
    const journal = {
      ...result.data,
      hand_in_on_time: hand_in_on_time,
    }

    methods.reset(journal)
    setCurrentJournal(result.data)
    setLockedForReview(result.data?.is_locked || false)
  }

  useEffect(() => {
    const substrings = location.pathname.split('/')
    const lastValue = substrings[substrings.length - 1]

    if (lastValue === '+') {
      if (location.state?.date) {
        const date = location.state.date
        methods.setValue('entry_date', date)
      }
      if (location.state?.apprentice_id) {
        methods.setValue('id_apprentice', location.state.apprentice_id)
      }

      return
    }

    const journal_id = parseInt(lastValue)
    if (isNaN(journal_id)) {
      console.warn('[Edit Learning Journal] The journal id is not a number')
    }

    fetchFromDb(journal_id)
  }, [location])

  const fetchShortJournals = async (): Promise<void> => {
    setIsLoading(true)
    const result = await handleRequest<learningJournal[]>(
      API_LEARNING_JOURNALS_SHORT(),
      RequestMethod.GET
    )

    if (!result.data || result.error) {
      console.warn('[Journal List] Could fetch the journals, an error ocurred')
      return
    }

    const fetchedJournals = result.data
    fetchedJournals.forEach((journal) =>
      dispatcher(addLearningJournal(journal))
    )
    setIsLoading(false)
  }

  useEffect(() => {
    if (loadedBasicInformation) {
      const thisEmployee = employees.find((emp) => emp.person_id === personId)
      if (thisEmployee) {
        methods.setValue('id_responsible', thisEmployee.person_id)
      }

      if (journals.length === 0 && !isLoading) {
        fetchShortJournals()
      }
    }
  }, [loadedBasicInformation])

  useEffect(() => {
    if (loadedBasicInformation && !isLoading) {
      const subscription = methods.watch(async (value, { type }) => {
        // undefined is needed for phone adds
        if (type === 'change') {
          setHasUnsavedChanges(true)
          const apprenticeId = value.id_apprentice
          const entry_date = value.entry_date
          monthlyDuplicate(apprenticeId, entry_date)
        }
      })
      return () => subscription.unsubscribe()
    }
  }, [loadedBasicInformation, isLoading])

  const saveJournalStateCb = (journal: learningJournal) => {
    setCurrentJournal(journal)
  }

  const onSubmit = async (
    formData: learningJournal,
    review: boolean = false
  ): Promise<boolean> => {
    if (formData.entry_date) {
      const dayjs_entry = dayjs(formData.entry_date)
      const year = dayjs_entry.year()
      const cw = dayjs_entry.week()
      formData.year = year
      formData.calendary_week = cw
      const sqlDate = getFormattedDateForMySQL(formData.entry_date)
      if (!sqlDate) {
        console.warn('[Journal Entry] Entry Date not valid')
        return false
      }
      formData.entry_date = sqlDate
    }

    // create new journal
    if (!currentJournal) {
      const result = await handleRequest<learningJournal>(
        API_LEARNING_JOURNALS(),
        RequestMethod.POST,
        formData
      )

      if (!result.data || result.error) {
        console.warn('[Learning Journal] Posting the journal failed')
        return false
      }
      const journalId = result.data.learning_journal_id
      // @ts-ignore
      result.data.hand_in_on_time = result.data?.hand_in_on_time !== 0
      methods.reset(result.data)
      setCurrentJournal({ ...result.data })
      window.history.replaceState(
        null,
        '',
        `${AuthRoutes.journals}/${journalId}`
      )
      dispatcher(addLearningJournal({ ...result.data, hand_in_on_time: true }))
      setHasUnsavedChanges(false)
      return false
    }

    if (!currentJournal.learning_journal_id) {
      // make the compiler happy
      console.warn('[Learning Journal] No journal id, cannot PUT')
      return false
    }
    let result: any
    let currentDataCopy = { ...currentJournal }
    if (
      hasRequiredClaim(userClaims, claims.learningJournals.edit.update) &&
      !review
    ) {
      result = await handleRequest<learningJournal>(
        API_LEARNING_JOURNAL_EDIT(currentJournal.learning_journal_id),
        RequestMethod.PUT,
        formData
      )

      if (!result.data || result.error) {
        if (result.httpStatus === HttpStatusCode.UNAUTHORIZED) {
          toast.current?.show({
            closable: true,
            severity: 'error',
            summary: t<string>('journal.submit.error.locked.title'),
            detail: t<string>('journal.submit.error.locked.message'),
            life: 4000,
          })
          return false
        }
        console.warn(
          '[Learning Journal] An error ocurred, was not able to update the journal'
        )
        return false
      }
      dispatcher(updateLearningJournal({ ...result.data }))

      currentDataCopy = { ...currentDataCopy, ...result.data }
    }

    if (hasRequiredClaim(userClaims, claims.learningJournals.review.update)) {
      result = await handleRequest<learningJournal>(
        API_LEARNING_JOURNAL_REVIEW(currentJournal.learning_journal_id),
        RequestMethod.PUT,
        formData
      )
      if (!result.data || result.error) {
        console.warn(
          '[Learning Journal] An error ocurred, was not able to update the journal'
        )
        return false
      }
      dispatcher(updateLearningJournal({ ...result.data }))
      currentDataCopy = { ...currentDataCopy, ...result.data }
    }

    const hand_in_on_time = result.data?.hand_in_on_time !== 0
    currentDataCopy = {
      ...currentDataCopy,
      hand_in_on_time: hand_in_on_time,
    }

    setCurrentJournal(currentDataCopy)
    methods.reset(currentDataCopy)
    setLockedForReview(result.data?.is_locked || false)
    setHasUnsavedChanges(false)
    return true
  }

  const clearForReview = async (): Promise<void> => {
    if (Object.keys(methods.formState.errors).length !== 0) {
      return
    }
    const formData: learningJournal = methods.getValues()
    formData.is_locked = true
    const success = await onSubmit(formData)
    if (success) {
      toast.current?.show({
        closable: true,
        severity: 'success',
        summary: t<string>('core.action.save.success.title'),
        detail: t<string>('journal.submit.review'),
        life: 4000,
      })
    }
  }

  const apprenticeButton = (): JSX.Element => {
    return !lockedForReview ? (
      <div className="pr-2">
        <Button
          type={'button'}
          className={'p-button-success'}
          label={t<string>('journal.clear_for_review')}
          onClick={clearForReview}
          tooltip={t<string>('journal.will_be_locked_on_submit')}
          tooltipOptions={{ position: 'top', showDelay: 800 }}
          disabled={lockedForReview}
        />
      </div>
    ) : (
      <></>
    )
  }

  const openForRevision = async () => {
    if (Object.keys(methods.formState.errors).length !== 0) {
      return
    }

    const formData: learningJournal = methods.getValues()
    const completed = formData.completed
    // if the form is completed we always open it for the berufsbildner
    // otherwise we switch between open and close
    formData.is_locked = formData.completed ? true : !formData.is_locked
    formData.completed = false
    const success = await onSubmit(formData, true)
    if (success) {
      if (!formData.is_locked) {
        toast.current?.show({
          closable: true,
          severity: 'success',
          summary: t<string>('core.action.save.success.title'),
          detail: t<string>('journal.submit.revision'),
          life: 4000,
        })
      } else if (completed) {
        toast.current?.show({
          closable: true,
          severity: 'success',
          summary: t<string>('core.action.save.success.title'),
          detail: t<string>('journal.submit.reopen'),
          life: 4000,
        })
      } else {
        toast.current?.show({
          closable: true,
          severity: 'success',
          summary: t<string>('core.action.save.success.title'),
          detail: t<string>('journal.submit.lock'),
          life: 4000,
        })
      }
    }
  }

  const closeJournal = async () => {
    if (Object.keys(methods.formState.errors).length !== 0) {
      return
    }
    const formData: learningJournal = methods.getValues()
    formData.is_locked = true
    formData.completed = true
    const success = await onSubmit(formData, true)
    if (success) {
      toast.current?.show({
        closable: true,
        severity: 'success',
        summary: t<string>('core.action.save.success.title'),
        detail: t<string>('journal.submit.close'),
        life: 4000,
      })
    }
  }

  const reviewerButton = (): JSX.Element => {
    return (
      <>
        <div className="pr-2">
          <Button
            type={'button'}
            className={'p-button-warning'}
            label={
              currentJournal?.completed
                ? t<string>('journal.open')
                : lockedForReview
                ? t<string>('journal.revision')
                : t<string>('journal.lock')
            }
            onClick={openForRevision}
          />
        </div>
        {!currentJournal?.completed && (
          <div className="pr-2">
            <Button
              type={'button'}
              className={'p-button-success'}
              label={t<string>('journal.close')}
              onClick={closeJournal}
            />
          </div>
        )}
      </>
    )
  }

  const saveButton = async (formData: learningJournal): Promise<void> => {
    // if we are locked, we call the review route, else the edit route
    // special case for the admin that is able to do everything
    const success = await onSubmit(formData, currentJournal?.is_locked)

    if (success) {
      toast.current?.show({
        closable: true,
        severity: 'success',
        summary: t<string>('core.action.save.success.title'),
        detail: t<string>('core.action.save.success.message'),
        life: 4000,
      })
    }
  }

  const checkDate = (date: string) => {
    const apprenticeId = methods.getValues('id_apprentice')
    const journalId = methods.getValues('learning_journal_id')
    const dayjsDate = dayjs(date)
    const dateMatch = journals.find(
      (journal) =>
        dayjsDate.isSame(journal.entry_date, 'week') &&
        journal.id_apprentice === apprenticeId &&
        ((journalId && journal.learning_journal_id !== journalId) || !journalId)
    )
    if (dateMatch !== undefined) {
      return t<string>('journal.form.errors.week_exists')
    }
  }

  const editorHeader = (): JSX.Element => {
    return (
      <span className="ql-formats">
        <select className="ql-header">
          <option value="0" />
          <option value="1" />
          <option value="2" />
          <option value="3" />
        </select>
        <select className="ql-font">
          <option value="sans-serif" />
          <option value="serif" />
          <option value="monospace" />
        </select>
        <button className="ql-bold" aria-label="Bold" />
        <button className="ql-italic" aria-label="Italic" />
        <button className="ql-underline" aria-label="Underline" />
        <select className="ql-color" />
        <select className="ql-background" />
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
        <button className="ql-code-block" />
        <button className="ql-script" value="sub" />
        <button className="ql-script" value="super" />
        <button className="ql-indent" value="-1" />
        <button className="ql-indent" value="+1" />
        <select className="ql-align" />
        {/*<button className="ql-link" />*/}
      </span>
    )
  }

  const readOnlyHeader = (): JSX.Element => {
    return <></>
  }

  const onJournalEntryDelete = async (): Promise<void> => {
    const journalId = methods.getValues('learning_journal_id')

    if (!journalId) {
      console.log(
        '[Learning Journals] Trying to delete even though there is no journal id present'
      )
      return
    }
    const result = await handleRequest<void>(
      API_LEARNING_JOURNAL(journalId),
      RequestMethod.DELETE
    )

    if (result.error) {
      toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('journal.delete_entry_fail.title'),
        detail: t<string>('journal.delete_entry_fail.message'),
        life: 12000,
      })
      console.warn(
        '[Learning Journal] Error occurred on deleting the file.',
        result.error
      )
      return
    }

    toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.delete.confirmed.title'),
      detail: t<string>('core.action.delete.confirmed.message'),
      life: 8000,
    })
    setTimeout(() => {
      if (!currentJournal) {
        console.log(
          '[Learning Journals] Trying to delete even though there is no journal id present'
        )
        return
      }
      dispatcher(deleteLearningJournal(currentJournal))
      navigate(AuthRoutes.journals)
    }, 1500)
  }

  const resetDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const monthlyDuplicate = (
    apprenticeId: number | undefined,
    entry_date: any
  ) => {
    const journalId = methods.getValues('learning_journal_id')
    const apprentice = findApprenticeById(apprenticeId, apprentices)
    const currentJournalEntryDate = dayjs(entry_date)
    if (apprentice?.apprentice?.journal_rythm !== JournalRythmCt.Monthly) {
      setShowMonthDuplicateWarning(false)
      return
    }
    const dateMatch = journals.find(
      (journal) =>
        currentJournalEntryDate.isSame(journal.entry_date, 'month') &&
        journal.id_apprentice === apprentice?.apprentice?.apprentice_id &&
        ((journalId && journal.learning_journal_id !== journalId) || !journalId)
    )
    setShowMonthDuplicateWarning(dateMatch !== undefined)
  }

  return (
    <div className={'card pl-2 pr-2'}>
      <div className={'w-full inline-flex justify-content-end'}>
        {hasUnsavedChanges && currentJournal !== undefined && (
          <>
            <span
              className={
                'icon-size-20 pulse-element mt-1 mr-1 fa-solid fa-exclamation-circle icon-orange'
              }
            />
            <Tooltip
              target={'.fa-exclamation-circle'}
              content={t<string>('core.action.leave_page_warning')}
            />
          </>
        )}
        <div>
          {currentApprentice?.person_id !== personId && (
            <BackButton
              translationKey={'journal.open_issues'}
              route={AuthRoutes.listJournals}
            />
          )}
          <BackButton
            translationKey={'navigation.go_back_overview'}
            route={AuthRoutes.journals}
          />
        </div>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(saveButton)}>
          <div className={'p-fluid formgrid grid pl-2'}>
            <div
              className={' col-12 formgroup-inline grid flex-wrap mt-1 mb-2'}
            >
              <div className={'md:col-4 col-12 '}>
                <ApprenticeDropdown
                  isDisabled={
                    !hasReviewClaim ||
                    currentJournal?.learning_journal_id !== undefined
                  }
                  fieldName={'id_apprentice'}
                />
              </div>

              <div className={'md:col-4 col-12 '}>
                <EmployeeDropdown
                  fieldName={'id_responsible'}
                  isCompulsory={true}
                  isDisabled={
                    (reviewDisabled || !reviewerCanEdit) &&
                    currentJournal !== undefined
                  }
                />
              </div>
              <div className={'md:col-4 col-12 '}>
                <div>
                  <label
                    htmlFor={'apprentice-dropdown'}
                    className={classNames({
                      'p-error': methods.formState.errors.entry_date,
                    })}
                  >
                    {t<string>('journal.entry_date')}
                  </label>
                  {showMonthDuplicateWarning && (
                    <>
                      <span
                        className={
                          'icon-size-20 pulse-element mt-1 mr-1 fa-solid fa-exclamation-circle icon-orange'
                        }
                      />
                      <Tooltip
                        target={'.fa-exclamation-circle'}
                        content={t<string>('journal.duplicate_month')}
                      />
                    </>
                  )}
                </div>

                <Controller
                  name={'entry_date'}
                  control={methods.control}
                  rules={{
                    required: getRequiredFormMessage('journal.entry_date'),
                    validate: (d) => checkDate(d),
                  }}
                  render={({ field, fieldState }) => (
                    <Calendar
                      {...field}
                      value={field.value ? new Date(field.value) : ''}
                      dateFormat={'dd.mm.yy'}
                      showIcon
                      disabled={
                        (reviewDisabled || !reviewerCanEdit) &&
                        currentJournal !== undefined
                      }
                      className={'people-dropdown-height'}
                      onChange={(e) => {
                        field.onChange(e.value)
                      }}
                      showWeek
                    />
                  )}
                />
                {getFormErrorMessage(methods.formState.errors, 'entry_date')}
              </div>
            </div>
            {currentJournal !== undefined && (
              <>
                <div className="pb-2 pr-3 w-full">
                  <Message
                    severity={
                      currentJournal.completed
                        ? 'success'
                        : hasReviewClaim && !currentJournal?.is_locked
                        ? 'warn'
                        : 'info'
                    }
                    text={helperMessages()}
                  ></Message>
                </div>
                <Fieldset
                  legend={t<string>('journal.journal_entry')}
                  toggleable
                  className="w-full mr-3 mt-2"
                >
                  <div className="col-12">
                    {apprenticeCanEdit ? (
                      <Controller
                        name={'journal_entry'}
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <Editor
                            style={{ height: '34vh', minHeight: '300px' }}
                            {...field}
                            value={field.value || ''}
                            onTextChange={(e) => {
                              field.onChange(e.htmlValue)
                            }}
                            headerTemplate={editorHeader()}
                          />
                        )}
                      />
                    ) : (
                      <Editor
                        className=""
                        style={{ height: '34vh', minHeight: '300px' }}
                        value={currentJournal.journal_entry}
                        readOnly
                        headerTemplate={readOnlyHeader()}
                      />
                    )}
                    <JournalFileDatatable
                      currentJournal={currentJournal}
                      editMode={reviewerCanEdit || apprenticeCanEdit}
                      deleteButtonDisabled={!showSaveButton}
                      personId={personId}
                      toast={toast}
                      setJournal={saveJournalStateCb}
                    />
                  </div>
                </Fieldset>
                <div className="col-12 pr-3 pt-2">
                  <label
                    htmlFor={'comment'}
                    className={classNames({
                      'p-error': methods.formState.errors.comment,
                    })}
                  >
                    {t<string>('journal.comment')}
                  </label>
                  {reviewerCanEdit ? (
                    <Controller
                      name={'comment'}
                      control={methods.control}
                      render={({ field }) => (
                        <Editor
                          className="pt-1"
                          style={{ height: '20vh', minHeight: '200px' }}
                          {...field}
                          value={field.value || ''}
                          onTextChange={(e) => field.onChange(e.htmlValue)}
                          headerTemplate={editorHeader()}
                        />
                      )}
                    />
                  ) : (
                    <Editor
                      className="pt-1"
                      style={{ height: '20vh', minHeight: '200px' }}
                      value={
                        currentJournal.comment
                          ? currentJournal.comment
                          : t<string>('journal.comment_after_hand_in')
                      }
                      readOnly
                      headerTemplate={readOnlyHeader()}
                    />
                  )}
                </div>
                <div className="col-12 pl-1 pt-2">
                  <div className="flex align-items-center">
                    <Controller
                      name={'hand_in_on_time'}
                      control={methods.control}
                      render={({ field }) => (
                        <Checkbox
                          className="pt-1"
                          {...field}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.checked)
                          }}
                          disabled={reviewDisabled || !reviewerCanEdit}
                        />
                      )}
                    />
                    <label className="ml-2 mt-2">
                      {t<string>('journal.hand_in_on_time')}
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="justify-content-end flex pb-4">
            {showSaveButton && (
              <Button
                className="mr-2"
                type={'submit'}
                label={
                  currentJournal
                    ? hasReviewClaim
                      ? t<string>('core.save')
                      : t<string>('core.save_draft')
                    : t<string>('journal.create')
                }
                disabled={!hasUnsavedChanges && currentJournal !== undefined}
              />
            )}
            {hasRequiredClaim(
              userClaims,
              claims.learningJournals.review.update
            ) &&
              currentJournal !== undefined &&
              reviewerButton()}
            {hasRequiredClaim(
              userClaims,
              claims.learningJournals.edit.update
            ) &&
              currentJournal !== undefined &&
              !hasAdminClaim &&
              apprenticeButton()}
            <div>
              {hasReviewClaim && currentJournal && (
                <Button
                  type={'button'}
                  icon="fa-solid fa-trash"
                  className="p-button-danger p-button-text ml-auto"
                  tooltip={t<string>('core.delete')}
                  tooltipOptions={{
                    style: { minWidth: '100px' },
                    showDelay: 500,
                  }}
                  onClick={() => setOpenDeleteDialog(true)}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
      <Toast ref={toast} position="bottom-right" />
      <ConfirmDialog
        visible={openDeleteDialog}
        header={t<string>('journal.delete_entry.title')}
        message={t<string>('journal.delete_entry.message')}
        onHide={resetDeleteDialog}
        reject={resetDeleteDialog}
        accept={onJournalEntryDelete}
      ></ConfirmDialog>
    </div>
  )
}